import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CircleAlert, CircleCheck, Loader2 } from 'lucide-react';
import { ApiError } from '../../lib/api';
import {
  composicionApi,
  composicionKeys,
  type ActividadArbol,
  type ArbolComposicion,
} from '../../lib/composicion';
import type { Herramienta } from '../../lib/sesion';

/**
 * Conexiones entre actividades (Lote 4): chips que muestran, por cada input
 * del manifiesto de la herramienta, de QUÉ actividad anterior va a recibir su
 * material — sea por binding explícito (actividad.config.bindings, persistido
 * vía PATCH /actividades/{id}/composicion) o por la resolución automática por
 * cercanía que aplica el backend cuando no hay binding.
 */

/** Input del manifiesto v2 (C3) con lo que los chips necesitan. */
export interface InputHerramienta {
  clave: string;
  origen?: string;
  de_captura?: string;
  de_familia?: string;
  requerido?: boolean;
  descripcion?: string;
}

/** Extrae los inputs[] tipados del manifiesto (forma laxa `Record<string, unknown>[]`). */
export function inputsDe(manifiesto: Herramienta | null | undefined): InputHerramienta[] {
  const crudos = manifiesto?.inputs ?? [];
  return crudos.filter(
    (i) => typeof i.clave === 'string' && i.clave !== '',
  ) as unknown as InputHerramienta[];
}

/**
 * True si la actividad puede ALIMENTAR alguno de estos inputs (familia de su
 * herramienta == de_familia y alguna captura declarada == de_captura). Es el
 * mismo criterio client-side que usa el backend, sin la parte de orden: sirve
 * para el halo «esto alimenta a lo que estás por soltar».
 */
export function satisfaceAlgunInput(
  actividad: ActividadArbol,
  inputs: InputHerramienta[],
): boolean {
  return inputs.some(
    (inp) =>
      inp.de_familia != null &&
      inp.de_captura != null &&
      actividad.herramienta.familia === inp.de_familia &&
      actividad.herramienta.capturas_tipos.includes(inp.de_captura),
  );
}

/**
 * Orden global client-side (espejo del backend): fases por orden, sesiones en
 * el orden estable en que vienen del árbol (fecha/hora_ini/id lo garantiza el
 * servidor), actividades por orden.
 */
function aplanarActividades(arbol: ArbolComposicion): ActividadArbol[] {
  const planas: ActividadArbol[] = [];
  for (const fase of [...arbol.fases].sort((a, b) => a.orden - b.orden)) {
    for (const sesion of fase.sesiones) {
      for (const act of [...sesion.actividades].sort((a, b) => a.orden - b.orden)) {
        planas.push(act);
      }
    }
  }
  return planas;
}

/** Recorte legible de la descripción del input para el chip de «le falta …». */
function descripcionCorta(inp: InputHerramienta): string {
  const d = inp.descripcion?.trim();
  if (!d) return `material para «${inp.clave}»`;
  const primera = d.split('.')[0].trim();
  return primera.length > 90 ? `${primera.slice(0, 90)}…` : primera;
}

const claseChip =
  'flex min-h-touch w-full items-center gap-1.5 rounded-md border border-border ' +
  'bg-surface-raised px-2 py-1 text-left font-sans text-xs text-content-muted ' +
  'transition-colors duration-fast hover:border-border-strong hover:text-content ' +
  'focus-visible:outline-none focus-visible:shadow-focus ' +
  'disabled:pointer-events-none disabled:opacity-50';

const claseSelect =
  'mt-1 w-full rounded-md border border-border-strong bg-surface-raised px-2 py-1 ' +
  'font-sans text-xs text-content transition-colors duration-fast ' +
  'focus-visible:outline-none focus-visible:shadow-focus';

/**
 * Chips de conexión de UNA actividad (visible solo si su herramienta declara
 * inputs[]). Cada chip dice de dónde recibe («recibe «X» de …», CircleCheck)
 * o qué le falta (CircleAlert). Clic → select compacto con las actividades
 * candidatas del árbol (anteriores en orden global, familia+captura) más
 * «Automática (la más cercana)», que borra el binding (null).
 */
export function ConexionInputs({
  actividad,
  procesoId,
  inputs,
}: {
  actividad: ActividadArbol;
  procesoId: string;
  inputs: InputHerramienta[];
}) {
  const qc = useQueryClient();

  // El árbol ya está en caché (lo trae CompositorPage): no viaja por props
  // para no tocar los componentes intermedios del lienzo.
  const arbol = useQuery({
    queryKey: composicionKeys.arbol(procesoId),
    queryFn: () => composicionApi.arbol(procesoId),
    staleTime: 30_000,
  });

  // Actividades ANTERIORES a esta en el orden global del proceso.
  const anteriores = useMemo(() => {
    if (!arbol.data) return [];
    const planas = aplanarActividades(arbol.data);
    const idx = planas.findIndex((a) => a.id === actividad.id);
    return idx < 0 ? [] : planas.slice(0, idx);
  }, [arbol.data, actividad.id]);

  // Clave del input cuyo select está desplegado (uno a la vez).
  const [abierta, setAbierta] = useState<string | null>(null);

  const editarMut = useMutation({
    mutationFn: (args: { clave: string; fuente: string | null }) =>
      composicionApi.editarActividad(actividad.id, {
        bindings: { [args.clave]: args.fuente },
      }),
    onSuccess: () => {
      setAbierta(null);
      void qc.invalidateQueries({ queryKey: composicionKeys.arbol(procesoId) });
    },
  });
  const accionError = (editarMut.error as ApiError | null)?.detail ?? null;

  if (inputs.length === 0) return null;

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {inputs.map((inp) => {
        const candidatas = anteriores.filter(
          (a) =>
            inp.de_familia != null &&
            inp.de_captura != null &&
            a.herramienta.familia === inp.de_familia &&
            a.herramienta.capturas_tipos.includes(inp.de_captura),
        );
        const bindingId = actividad.bindings?.[inp.clave] ?? null;
        const explicita = bindingId
          ? candidatas.find((a) => a.id === bindingId) ?? null
          : null;
        // Sin binding (o binding huérfano tras mover cosas): la automática es
        // la candidata MÁS CERCANA hacia atrás — la última de la lista plana.
        const fuente = explicita ?? candidatas[candidatas.length - 1] ?? null;
        const desplegado = abierta === inp.clave;

        return (
          <div key={inp.clave}>
            <button
              type="button"
              onClick={() => setAbierta(desplegado ? null : inp.clave)}
              disabled={editarMut.isPending}
              title={inp.descripcion ?? `Conexión del input «${inp.clave}»`}
              aria-expanded={desplegado}
              className={claseChip}
            >
              {editarMut.isPending && abierta === inp.clave ? (
                <Loader2
                  className="h-3.5 w-3.5 shrink-0 animate-spin text-content-muted"
                  aria-hidden="true"
                />
              ) : fuente ? (
                <CircleCheck className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
              ) : (
                <CircleAlert className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
              )}
              <span className="min-w-0 flex-1 truncate">
                {fuente
                  ? `recibe «${inp.clave}» de ${fuente.herramienta.nombre}`
                  : `le falta ${descripcionCorta(inp)}`}
              </span>
            </button>

            {desplegado &&
              (candidatas.length > 0 ? (
                <select
                  value={explicita ? bindingId ?? '' : ''}
                  onChange={(e) =>
                    editarMut.mutate({
                      clave: inp.clave,
                      fuente: e.target.value === '' ? null : e.target.value,
                    })
                  }
                  disabled={editarMut.isPending}
                  aria-label={`Actividad fuente del input «${inp.clave}»`}
                  className={claseSelect}
                >
                  <option value="">Automática (la más cercana)</option>
                  {candidatas.map((c, i) => (
                    <option key={c.id} value={c.id}>
                      {i + 1}. {c.herramienta.nombre}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="mt-1 font-sans text-xs text-content-muted">
                  Agregá antes una actividad de {inp.de_familia ?? 'esa familia'} que
                  capture {inp.de_captura ?? 'ese material'}: su resultado alimenta
                  este paso.
                </p>
              ))}
          </div>
        );
      })}

      {accionError && (
        <p className="flex items-start gap-1.5 font-sans text-xs text-error">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {accionError}
        </p>
      )}
    </div>
  );
}
